import styled, { css, useTheme } from "styled-components";
import AntSelect, { SelectProps as AntSelectProps } from "antd/es/select";
import { SelectValue } from "antd/es/select";
import { FixMeLater } from "types";
import { ReactElement, useEffect, useState } from "react";
import { hexToRGB } from "@shared/utils/misc";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import { useBottomNavigator } from "utils/hooks/misc";
import MobileSelect from "common/Select/MobileSelect";
import CloseCircleFilled from "@ant-design/icons/CloseCircleFilled";

interface SelectProps<VT> extends AntSelectProps<VT> {
  flushPaddingLeft?: boolean;
  error?: boolean;
  rounded?: boolean;
  alwaysShowClear?: boolean;
  hideArrow?: boolean;
  alignRight?: boolean;
  mobileTitle?: string;
  triggerElement?: ReactElement;
  bottomExtraElement?: JSX.Element;
}

const Select = <VT extends SelectValue = SelectValue>({
  flushPaddingLeft,
  error,
  styles,
  rounded,
  alwaysShowClear,
  hideArrow,
  alignRight,
  mobileTitle,
  open: propsOpen,
  onOpenChange: propsOnOpenChange,
  triggerElement,
  bordered,
  bottomExtraElement,
  ...props
}: SelectProps<VT>) => {
  const appTheme = useTheme();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const [mobileOpen, setMobileOpen] = useState(propsOpen);
  const bottomNavigatorData = useBottomNavigator();
  const isMobileSelect =
    bottomNavigatorData.baseShouldDisplay && !!props.options;
  const open = propsOpen ?? (isMobileSelect ? mobileOpen : undefined);
  const allowClear =
    typeof props.allowClear === "boolean" && props.allowClear
      ? { clearIcon: <ClearIcon /> }
      : props.allowClear;

  useEffect(() => {
    if (isMobileSelect) {
      setMobileOpen(propsOpen);
    }
  }, [propsOpen]);

  const onOpenChange = (open: boolean) => {
    propsOnOpenChange?.(open);
    if (isMobileSelect) {
      setMobileOpen(open);
    }
  };

  const updatedStyles = {
    ...styles,
    popup: {
      ...styles?.popup,
      root: {
        ...styles?.popup?.root,
        background: appTheme.colors.cardBgColor,
      },
    },
  };

  return (
    <>
      {isMobileSelect && (
        <MobileSelect
          open={open}
          onOpenChange={onOpenChange}
          mobileTitle={mobileTitle}
          bottomExtraElement={bottomExtraElement}
          {...props}
        />
      )}
      {triggerElement ?? (
        <StyledSelect
          open={isMobileSelect ? false : open}
          onOpenChange={onOpenChange}
          $flushPaddingLeft={flushPaddingLeft}
          $error={error}
          $rounded={rounded}
          $alwaysShowClear={alwaysShowClear}
          $hideArrow={hideArrow}
          $alignRight={alignRight}
          dropdownAlign={{
            overflow: {
              adjustX: true,
              adjustY: true,
            },
          }}
          variant={bordered === false ? "borderless" : undefined}
          showSearch={
            !isMobileSelect && (!isMobile || (props.options?.length || 0) > 30)
          }
          {...props}
          styles={updatedStyles}
          classNames={{
            popup: { root: appTheme.isDarkMode ? "dark-mode" : "" },
          }}
          allowClear={allowClear}
        />
      )}
    </>
  );
};

Select.Option = AntSelect.Option;
Select.OptGroup = AntSelect.OptGroup;

export default Select;

export const selectStyle = css`
  .ant-select-item-option-selected > .ant-select-item-option-content {
    padding-right: 8px;
  }

  .ant-select-dropdown.dark-mode {
    ::-webkit-scrollbar {
      width: 15px;
    }

    ::-webkit-scrollbar-track {
      background-color: transparent;
    }

    ::-webkit-scrollbar-thumb {
      border: 4px solid transparent;
      background-color: ${(p) => hexToRGB(p.theme.colors.secondaryGrey, 0.8)};
      border-radius: 100px;
      background-clip: content-box;
    }

    .ant-select-item-option-content {
      color: ${(p) => p.theme.colors.primaryGrey};
    }

    .ant-empty-description {
      color: ${(p) => p.theme.colors.secondaryGrey};
    }

    .ant-select-item-option-active {
      background: ${(p) => p.theme.colors.lightestGrey};
    }

    .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
      background: ${(p) => p.theme.colors.lightestGrey};
    }

    .ant-select-item-group {
      color: ${(p) => p.theme.colors.secondaryGrey};
    }
  }

  .ant-select {
    color: ${(p) => p.theme.colors.primaryText};
  }

  .ant-select-arrow {
    color: ${(p) => p.theme.colors.secondaryGrey};
  }

  .ant-select-multiple.ant-select-show-arrow .ant-select-selector,
  .ant-select-multiple.ant-select-allow-clear .ant-select-selector {
    .ant-select-selection-placeholder {
      margin-right: -24px;
    }
  }

  .ant-select-selector {
    &:not(.ant-select-customize-input) {
      background: ${(p) => p.theme.colors.cardBgColor};
    }
  }

  .ant-select:not(.ant-select-borderless) {
    .ant-select-selector:not(.ant-select-customize-input) {
      border-color: ${(p) => p.theme.colors.lightBorderGrey};

      &:hover {
        border-color: ${(p) => p.theme.colors.primaryBlue};
      }
    }
  }

  .ant-select-selection-placeholder {
    color: ${(p) => p.theme.colors.lightestGrey};
  }

  &.ant-select-multiple .ant-select-selection-item {
    background: ${(p) => p.theme.colors.lightBackground};
    border-color: ${(p) => p.theme.colors.lightBorderGrey};

    .anticon-close {
      color: ${(p) => p.theme.colors.basic};
    }
  }

  &.ant-select:not(.ant-select-disabled):not(.ant-select-borderless):hover
    .ant-select-selector {
    border-color: ${(p) => p.theme.colors.primaryBlue} !important;
  }

  .ant-select-focused:not(.ant-select-disabled).ant-select:not(
      .ant-select-customize-input
    ):not(.ant-select-borderless)
    .ant-select-selector {
    border-color: ${(p) => p.theme.colors.primaryBlue} !important;
    box-shadow: 0 0 0 2px ${(p) => hexToRGB(p.theme.colors.primaryBlue, 0.2)};
  }

  .ant-select-dropdown {
    background: ${(p) => p.theme.colors.cardBgColor};
  }

  .ant-select-item-option-selected:not(.ant-select-item-option-disabled)
    .ant-select-item-option-state {
    color: ${(p) => p.theme.colors.primaryBlue};
  }

  .ant-select-item-option-content,
  .ant-select-selection-item {
    color: ${(p) => p.theme.colors.primaryText};
    display: flex !important;
    align-items: center;
    justify-content: flex-start;

    & > div {
      max-width: 100%;
    }
  }

  .ant-select-item-option-active {
    background: ${(p) => p.theme.colors.tableHoverBgColor};
  }

  .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    background: ${(p) => p.theme.colors.titleCardBgColor};
  }

  .ant-select-item-group {
    color: ${(p) => p.theme.colors.secondaryGrey};
  }

  &.ant-select.ant-select-disabled.ant-select:not(.ant-select-customize-input)
    .ant-select-selector {
    color: ${(p) => p.theme.colors.basic};
    background: ${(p) => p.theme.colors.cardBgColor};
    opacity: 0.5;
  }

  .ant-select-clear {
    background: ${(p) => p.theme.colors.cardBgColor};
    color: ${(p) => p.theme.colors.secondaryGrey};
  }

  .ant-select-selector {
    border-radius: 6px;
  }

  .ant-select-single .ant-select-selector {
    color: ${(p) => p.theme.colors.primaryText};
  }

  .ant-select-single.ant-select-open .ant-select-selection-item {
    color: ${(p) => p.theme.colors.secondaryGrey};
  }
`;

const StyledSelect = styled(AntSelect)<FixMeLater>`
  ${(props) =>
    props.$flushPaddingLeft &&
    `
    .ant-select-selector {
      padding-left: 0 !important;
    }
  `}
  ${(props) =>
    props.$error &&
    `
    .ant-select-selection-item {
      color: ${props.theme.colors.red};
    }
  `}
  ${(p) =>
    p.$rounded &&
    `
    .ant-select-selector {
      border-radius: 8px !important;
    }
  `};
  ${(p) =>
    p.$alwaysShowClear &&
    `
     .ant-select-clear {
      opacity: 1
     }
  `};
  ${(p) =>
    p.$hideArrow &&
    `
     .ant-select-arrow {
        display: none;
     }
  `};
  ${(p) =>
    p.$alignRight &&
    `
     .ant-select-selection-placeholder{
         right: 0;
     }
  `};
  ${selectStyle};
`;
const ClearIcon = styled(CloseCircleFilled)`
  padding: 8px;
  margin: -8px;
`;
